/* lib/api/academy.js — клиент «Академии» (обучающая платформа).
 * Контракт как у остальных клиентов: unwrap(apiFetch(...)) → data | throw err{status,code,detail}.
 * Файлы (видео/материалы/обложка/вложения ДЗ) шлём multipart (FormData). */
import { apiFetch, downloadFile } from './client.js';
import { unwrap } from './unwrap.js';

function fileForm(file, field = 'file') {
  const fd = new FormData();
  fd.append(field, file);
  return fd;
}

// Гард удаления контента с работами учеников: бэк отвечает 400 code='has_student_data'
// и требует повтора с ?force=1 (views._student_data_guard). Консоль обязана распознать
// именно этот отказ — иначе методист читает инструкцию, которую не может выполнить.
export const STUDENT_DATA_CODE = 'has_student_data';
export function isStudentDataGuard(e) {
  return !!e && e.code === STUDENT_DATA_CODE;
}
// unwrap прокидывает в ошибку лишь белый список полей, а гард присылает СВОЙ счётчик
// затронутых учеников (`students`) — забираем его из сырого тела ответа, иначе диалог
// удаления не сможет назвать число и спросит вслепую.
async function unwrapDelete(promise) {
  const res = await promise; // apiFetch не бросает — всегда {ok,status,data}
  try {
    return await unwrap(Promise.resolve(res));
  } catch (e) {
    const d = res && res.data;
    if (d && typeof d === 'object' && Number.isFinite(d.students)) e.students = d.students;
    throw e;
  }
}
// Число затронутых учеников для текста диалога; null — бэк счётчика не прислал.
export function guardAffected(e) {
  const n = e && (Number.isFinite(e.students) ? e.students : e.count);
  return Number.isFinite(n) ? n : null;
}
/* Русское числительное: 1 ученик · 2-4 ученика · 5-20 учеников. Держим здесь, рядом
   с текстами консоли, потому что счётчики («У N учеников есть работы», «N учеников»
   в шапке курса) считаются в разных экранах, а склонение обязано быть одно.
   Исключение 11-14 — не опечатка: «11 учеников», хотя 11 % 10 === 1. */
export function pluralRu(n, one, few, many) {
  const abs = Math.abs(Number(n) || 0);
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = abs % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
export const studentsWord = (n) => pluralRu(n, 'ученик', 'ученика', 'учеников');
export const studentsWordGen = (n) => pluralRu(n, 'ученика', 'учеников', 'учеников');
export const lessonsWord = (n) => pluralRu(n, 'урок', 'урока', 'уроков');
// Родительный падеж — для конструкции «X из Y уроков». После «из» стоит генитив:
// «из 1 урока», «из 2 уроков», «из 5 уроков». Плеер печатал «уроков» жёстко и
// согласовывал слово с ПЕРВЫМ числом, поэтому у курса из одного урока выходило
// «0 из 1 уроков». Зеркалит studentsWordGen — тот же приём для «ученика».
export const lessonsWordGen = (n) => pluralRu(n, 'урока', 'уроков', 'уроков');
export const modulesWord = (n) => pluralRu(n, 'модуль', 'модуля', 'модулей');
export const coursesWord = (n) => pluralRu(n, 'курс', 'курса', 'курсов');
export const leadsWord = (n) => pluralRu(n, 'заявка', 'заявки', 'заявок');

// Текст подтверждения удаления «вместе с работами»: entity — родительный падеж
// («этому уроку», «этому модулю»).
export function studentDataPrompt(e, entity) {
  const n = guardAffected(e);
  // «У 1 учеников есть работы» методист читал как баг в счётчике и переспрашивал.
  return (n != null ? `У ${n} ${studentsWordGen(n)} есть работы` : 'У учеников есть работы')
    + ` по ${entity}. Удалить вместе с их результатами?`;
}

// ── ученик ────────────────────────────────────────────────────────────────────
export const getCatalog = () => unwrap(apiFetch('/api/academy/catalog/'));
export const getMyCourses = () => unwrap(apiFetch('/api/academy/my/'));
export const getCourseTree = (id) => unwrap(apiFetch(`/api/academy/courses/${id}/`));
export const enroll = (id) =>
  unwrap(apiFetch(`/api/academy/courses/${id}/enroll/`, { method: 'POST' }));
// покупка платного курса. opts: число/строка = legacy tariffId; объект =
// { tariffId, consents:{offer,privacy,reviews,mailings}, phone }. Бэк ТРЕБУЕТ
// согласий (оферта+политика) — иначе 400 code='consent_required' (#9).
export const checkoutCourse = (id, opts = {}) => {
  const o = (opts && typeof opts === 'object') ? opts : { tariffId: opts };
  const body = {};
  if (o.tariffId) body.tariff_id = o.tariffId;
  if (o.consents) body.consents = o.consents;
  if (o.phone) body.phone = o.phone;
  return unwrap(apiFetch(`/api/academy/courses/${id}/checkout/`, { method: 'POST', body }));
};
// Публичный лендинг курса по slug — БЕЗ авторизации (AllowAny); с сессией бэк
// добавит my_enrollment. Один и тот же эндпоинт для анонима и авторизованного (#16).
export const getPublicLanding = (slug) =>
  unwrap(apiFetch(`/api/academy/public/courses/${encodeURIComponent(slug)}/landing/`));
// Заявка с публичного лендинга (CTA-блок в режиме «форма») → лид в CRM. БЕЗ авторизации
// (AllowAny, rate-limit по IP). Возвращаем СЫРОЙ {ok,status,data} (НЕ unwrap): вызывающий
// сам разбирает 200 {ok,lead_id} / 400 (DRF {field:[msg]}) / 429 / 404, чтобы показать
// ошибки по полям и rate-limit прямо в форме (#16, B5).
export const submitLandingForm = (slug, payload) =>
  apiFetch(`/api/academy/public/courses/${encodeURIComponent(slug)}/landing/form/`,
    { method: 'POST', body: payload });
// PL-12: в ответе, кроме статуса, есть `kind` ('purchase' | 'upgrade'),
// `tariff_title` и `from_tariff_title` — страница оплаты обязана называть доплату
// доплатой, а не «покупкой курса», который у человека уже открыт.
export const getPurchaseStatus = (invoiceId) =>
  unwrap(apiFetch(`/api/academy/purchases/${invoiceId}/`));
// ST-13: покупки ученика (MyPurchasesView). Ответ — ПЛОСКИЙ массив строк
// {id,status,amount_rub,created_at,paid_at,invoice_id,course,tariff,can_retry}.
// Ключ именно `can_retry` (MyPurchaseSerializer.get_can_retry): по нему висящий
// заказ отличим от брошенного. Комментарий тут — единственная документация
// контракта, и он назывался `can_pay` — имени, которого в ответе нет.
export const getMyPurchases = () => unwrap(apiFetch('/api/academy/my/purchases/'));
export const mockCompletePurchase = (invoiceId, outcome) =>
  unwrap(apiFetch(`/api/academy/purchases/${invoiceId}/mock/`, { method: 'POST', body: { outcome } }));
export const getLesson = (id) => unwrap(apiFetch(`/api/academy/lessons/${id}/`));
export const markViewed = (id) =>
  unwrap(apiFetch(`/api/academy/lessons/${id}/view/`, { method: 'POST' }));
// Хартбит просмотра урока (B6): шлём ПРИРАЩЕНИЕ секунд с прошлого пинга (не накопительно),
// бэк клампит до HEARTBEAT_MAX_DELTA (≈120с). Best-effort — ошибки игнорируем у вызывающего.
export const heartbeat = (lessonId, deltaSeconds) =>
  unwrap(apiFetch(`/api/academy/lessons/${lessonId}/heartbeat/`,
    { method: 'POST', body: { delta_seconds: deltaSeconds } }));
// Обёртки над POST lessons/<id>/complete/ здесь НЕТ намеренно: контентный урок
// закрывается сам при открытии (mark_viewed → recompute_lesson), поэтому кнопку
// «Отметить пройденным» убрали как мёртвую аффордансу. Эндпоинт на бэке остаётся
// (у него свои тесты), но клиент, который никто не зовёт, — это обещание кнопки,
// которой нет.
export const submitQuiz = (quizId, answers) =>
  unwrap(apiFetch(`/api/academy/quizzes/${quizId}/submit/`,
    { method: 'POST', body: { answers } }));
// ST-5: ученик просит куратора сбросить попытки теста. Самосброса у ученика нет
// намеренно (иначе max_attempts теряет смысл) — это заявка в очередь куратора.
// Идемпотентно на бэке: повторное нажатие возвращает ту же заявку.
export const requestQuizRetake = (quizId, comment) =>
  unwrap(apiFetch(`/api/academy/quizzes/${quizId}/retake-request/`,
    { method: 'POST', body: comment ? { comment } : {} }));
// Публичный сертификат — открыт без авторизации (по public_id из ссылки).
export const getCertificate = (publicId) =>
  unwrap(apiFetch(`/api/academy/certificate/${publicId}/`));
/* ST-14: сертификат файлом. Сервер собирает PDF тем же составом, что на экране.
   Качаем через downloadFile (канон скачиваний проекта), а не window.open: тут это
   даёт не workspace-заголовок (сертификат публичный, пространство ему не нужно),
   а РАЗБОР ОТКАЗА — при 404 отозванного сертификата навигация показала бы белую
   вкладку с JSON, а не человеческую причину. Контракт downloadFile — {ok,status,
   data} без throw, разбирает вызывающий. */
export const certificatePdfUrl = (publicId) =>
  `/api/academy/certificate/${publicId}/pdf/`;
export const downloadCertificatePdf = (publicId) =>
  downloadFile(certificatePdfUrl(publicId));
export const submitHomework = (hwId, body, files) => {
  if (Array.isArray(files) && files.length) {
    const fd = new FormData();
    if (body) fd.append('body', body);
    files.forEach((f) => fd.append('files', f));
    return unwrap(apiFetch(`/api/academy/homework/${hwId}/submit/`, { method: 'POST', body: fd }));
  }
  return unwrap(apiFetch(`/api/academy/homework/${hwId}/submit/`, { method: 'POST', body: { body } }));
};

// ── куратор ─────────────────────────────────────────────────────────────────────
export const getReviewQueue = (params = {}) => {
  // params: { status, course_id, submitted_after, reviewed_after, q, limit, offset }.
  // Строка = статус (бэк-совместимость).
  const p = typeof params === 'string' ? { status: params } : (params || {});
  const qs = Object.entries(p)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return unwrap(apiFetch('/api/academy/review/submissions/' + (qs ? `?${qs}` : '')));
};
// Очередь приходит либо ПЛОСКИМ массивом (старый контракт, жёсткий срез 500), либо
// страницей LimitOffset {count,results} (CU-2). Приводим к одному виду: счётчик на табе
// обязан браться из count — на длине массива он залипал на 500 и хвост был недостижим.
export function normalizeQueue(data) {
  if (Array.isArray(data)) return { items: data, count: data.length, paginated: false };
  const items = Array.isArray(data && data.results) ? data.results : [];
  const count = typeof (data && data.count) === 'number' ? data.count : items.length;
  return { items, count, paginated: true };
}
export const getReviewCourses = () => unwrap(apiFetch('/api/academy/review/courses/'));

// ── ST-5: пересдачи теста (куратор и методист) ─────────────────────────────────
// Все три эндпоинта под review/ — гейт IsAcademyCurator пропускает и методиста,
// поэтому второй (manage/) путь клиенту не нужен: один URL на обе роли.
export const getRetakeRequests = (params = {}) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return unwrap(apiFetch('/api/academy/review/retake-requests/' + (qs ? `?${qs}` : '')));
};
export const declineRetakeRequest = (id, comment) =>
  unwrap(apiFetch(`/api/academy/review/retake-requests/${id}/decline/`,
    { method: 'POST', body: comment ? { comment } : {} }));
// Сброс попыток теста ученику. Уже СДАННЫЙ тест бэк не сбрасывает без force:
// отвечает 400 code='quiz_passed', т.к. сброс откатывает зачёт по уроку и статус
// курса. Схема та же, что у has_student_data: показать подтверждение и повторить.
export const resetQuizAttempts = (enrollmentId, quizId, force) =>
  unwrap(apiFetch(
    `/api/academy/review/enrollments/${enrollmentId}/quizzes/${quizId}/reset/`
    + (force ? '?force=1' : ''), { method: 'POST' }));
export const QUIZ_PASSED_CODE = 'quiz_passed';
export function isQuizPassedGuard(e) {
  return !!e && e.code === QUIZ_PASSED_CODE;
}
export const getReview = (id) => unwrap(apiFetch(`/api/academy/review/submissions/${id}/`));
export const reviewSubmission = (id, payload) =>
  unwrap(apiFetch(`/api/academy/review/submissions/${id}/review/`, { method: 'POST', body: payload }));
// CU-4: откат ошибочного вердикта. Причина ОБЯЗАТЕЛЬНА (её увидит и ученик, и
// следующий проверяющий) — журнал SubmissionReview append-only, переоткрытие
// добавляет третью запись, а не стирает вторую.
export const reopenReview = (id, reason) =>
  unwrap(apiFetch(`/api/academy/review/submissions/${id}/reopen/`,
    { method: 'POST', body: { reason } }));

/* CU-3. Конвейер проверки: после вердикта берём СЛЕДУЮЩУЮ непроверенную работу
   тем же запросом очереди (status=submitted, limit=1) — проверенная из выборки
   уже выпала, поэтому count в ответе и есть остаток очереди. Возвращаем
   { next, remaining }; next=null — очередь пуста.
   `currentId` защищает от гонки: если бэк ещё отдаёт только что проверенную
   работу (репликация/кэш), не зацикливаемся на ней. */
export function pickNextSubmission(data, currentId) {
  const page = normalizeQueue(data);
  const next = page.items.find((s) => s && s.id !== currentId) || null;
  return { next, remaining: page.count };
}
export async function fetchNextSubmission(filters = {}, currentId = null) {
  // limit=2 — ровно чтобы currentId, если он ещё в выборке, не съел единственную строку.
  const data = await getReviewQueue({ ...filters, status: 'submitted', limit: 2, offset: 0 });
  return pickNextSubmission(data, currentId);
}

// ── методист: курсы ──────────────────────────────────────────────────────────────
export const listCourses = (status) =>
  unwrap(apiFetch('/api/academy/manage/courses/' + (status ? `?status=${status}` : '')));
export const createCourse = (payload) =>
  unwrap(apiFetch('/api/academy/manage/courses/', { method: 'POST', body: payload }));
export const getCourse = (id) => unwrap(apiFetch(`/api/academy/manage/courses/${id}/`));
// тарифы (B1)
export const listTariffs = (courseId) => unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/tariffs/`));
// Планы подписки для тарифа-пропуска. Отдельная вью Академии, а не витринная:
// та прячет ЗАКРЫТЫЕ планы, а именно их и надо уметь назвать поимённо.
export const listSubscriptionPlans = () => unwrap(apiFetch('/api/academy/manage/subscription-plans/'));
export const createTariff = (courseId, payload) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/tariffs/`, { method: 'POST', body: payload }));
export const updateTariff = (id, payload) =>
  unwrap(apiFetch(`/api/academy/manage/tariffs/${id}/`, { method: 'PATCH', body: payload }));
export const deleteTariff = (id, force) =>
  unwrap(apiFetch(`/api/academy/manage/tariffs/${id}/` + (force ? '?force=1' : ''), { method: 'DELETE' }));
// Активные тарифы курса глазами УЧЕНИКА (массив). PL-12: у каждой строки есть ключ
// `upgrade` — контекст ДОПЛАТЫ для текущего пользователя:
//   null                       — доплата не про него (доступа нет / истёк → обычная
//                                покупка или продление по полной цене);
//   {available, price_rub, is_current, reason, from_tariff_id, from_tariff_title}
//                              — ученик учится по тарифу этого курса; `price_rub` —
//                                РАЗНИЦА цен, ровно та сумма, которую спишет чекаут
//                                (её считает один и тот же `purchasing.upgrade_offer`).
// `reason`: 'same_tariff' | 'downgrade' | 'upgrade_no_base' — почему нельзя.
export const getCourseTariffs = (courseId) => unwrap(apiFetch(`/api/academy/courses/${courseId}/tariffs/`));
// копирование/перенос контента (A2)
export const copyCourse = (id, title) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${id}/copy/`, { method: 'POST', body: title ? { title } : {} }));
export const copyModule = (id, targetCourseId) =>
  unwrap(apiFetch(`/api/academy/manage/modules/${id}/copy/`, { method: 'POST', body: targetCourseId ? { target_course_id: targetCourseId } : {} }));
// перенос модуля в другой курс (#2)
export const moveModule = (id, targetCourseId) =>
  unwrap(apiFetch(`/api/academy/manage/modules/${id}/move/`, { method: 'POST', body: { target_course_id: targetCourseId } }));
// предпросмотр лендинга из ЧЕРНОВИКА (методист) — для отдельной страницы превью (#16)
export const getLandingPreview = (courseId) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/landing-preview/`));
export const copyLesson = (id, targetModuleId) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/copy/`, { method: 'POST', body: targetModuleId ? { target_module_id: targetModuleId } : {} }));
export const moveLesson = (id, targetModuleId) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/move/`, { method: 'POST', body: { target_module_id: targetModuleId } }));
export const updateCourse = (id, payload) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${id}/`, { method: 'PATCH', body: payload }));
export const deleteCourse = (id, force) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${id}/` + (force ? '?force=1' : ''), { method: 'DELETE' }));
export const uploadCover = (id, file) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${id}/cover/`, { method: 'POST', body: fileForm(file) }));
// AD-7: картинка блока лендинга (LandingImageUploadView). Ответ — {url} с
// АБСОЛЮТНЫМ адресом: валидатор лендинга принимает только http(s).
export const uploadLandingImage = (courseId, file) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/landing-image/`,
    { method: 'POST', body: fileForm(file) }));

// ── модули ──────────────────────────────────────────────────────────────────────
export const createModule = (courseId, payload) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/modules/`, { method: 'POST', body: payload }));
export const updateModule = (id, payload) =>
  unwrap(apiFetch(`/api/academy/manage/modules/${id}/`, { method: 'PATCH', body: payload }));
export const deleteModule = (id, force) =>
  unwrapDelete(apiFetch(`/api/academy/manage/modules/${id}/` + (force ? '?force=1' : ''), { method: 'DELETE' }));
export const reorderModules = (courseId, ids) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/modules/reorder/`, { method: 'POST', body: { ids } }));

// ── уроки ─────────────────────────────────────────────────────────────────────
export const createLesson = (moduleId, payload) =>
  unwrap(apiFetch(`/api/academy/manage/modules/${moduleId}/lessons/`, { method: 'POST', body: payload }));
export const getManageLesson = (id) => unwrap(apiFetch(`/api/academy/manage/lessons/${id}/`));
export const updateLesson = (id, payload) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/`, { method: 'PATCH', body: payload }));
export const deleteLesson = (id, force) =>
  unwrapDelete(apiFetch(`/api/academy/manage/lessons/${id}/` + (force ? '?force=1' : ''), { method: 'DELETE' }));
export const reorderLessons = (moduleId, ids) =>
  unwrap(apiFetch(`/api/academy/manage/modules/${moduleId}/lessons/reorder/`, { method: 'POST', body: { ids } }));
export const uploadVideo = (id, file) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/video/`, { method: 'POST', body: fileForm(file) }));
export const deleteVideo = (id) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/video/`, { method: 'DELETE' }));
/* Превью (poster) видео урока. Клиентской проверки размера тут НЕТ намеренно:
   потолок общий с материалами и обложкой (ACADEMY_MATERIAL_MAX_BYTES, 500 МБ),
   наружу он не отдаётся, а завести во фронте четвёртую копию числа — верный
   способ получить подсказку, которая разъедется с сервером (ровно это уже было
   с видео: интерфейс обещал 512 МБ при фактических 300). Картинка-заставка в
   полсотни мегабайт — случай теоретический, и отказ сервера доедет до тоста. */
export const uploadVideoPoster = (id, file) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/video-poster/`, { method: 'POST', body: fileForm(file) }));
export const deleteVideoPoster = (id) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/video-poster/`, { method: 'DELETE' }));
/* Точный список форматов вместо `video/*`. С `video/*` диалог выбора предлагал
   .mkv/.avi наравне с остальными, сервер их не принимает (браузер такое видео не
   проигрывает — см. ACADEMY_VIDEO_ALLOWED_EXTENSIONS в settings.py), и методист
   узнавал об этом ПОСЛЕ того, как отправил на сервер до 1,5 ГБ: по мобильному
   интернету это десятки минут в никуда. Расширения И mime-типы вместе: mime у
   .m4v/.mov разные в разных ОС, а по одному расширению iOS-Safari фильтрует
   ненадёжно — вместе они дают самый широкий верный набор.
   Зеркало серверного allowlist: правя один список, править и другой. */
export const VIDEO_EXTS = ['mp4', 'mov', 'webm', 'm4v', 'ogv', 'ogg'];
/* Контейнеры, которые браузер не играет, а мы ПРИНИМАЕМ и перекодируем в MP4
   фоновой задачей (settings.ACADEMY_VIDEO_CONVERT_EXTENSIONS). В диалоге выбора
   они предлагаются наравне с готовыми: методисту не нужно знать, какой из его
   файлов сервер сконвертирует, — ему нужно, чтобы урок заработал.
   Если на сервере нет ffmpeg, приём вернётся к отказу с объяснением — тогда
   `accept` шире фактических возможностей, и это меньшее из двух зол: обратный
   промах (не дать выбрать файл, который сервер бы взял) методист исправить не
   может вообще. */
export const VIDEO_CONVERT_EXTS = ['mkv', 'avi', 'wmv', 'flv', 'mpg', 'mpeg',
  'ts', 'm2ts', 'mts', 'vob', '3gp', 'divx', 'asf', 'rm', 'rmvb'];
const PLAYABLE_ACCEPT = [
  ...VIDEO_EXTS.map((e) => `.${e}`),
  'video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v', 'video/ogg',
];
const CONVERT_ACCEPT = [
  ...VIDEO_CONVERT_EXTS.map((e) => `.${e}`),
  'video/x-matroska', 'video/x-msvideo', 'video/mp2t', 'video/x-ms-asf',
  'application/vnd.rn-realmedia',
];
/* Полный список — когда сервер умеет конвертировать (user.academy_video_convert
   из /me). Без этого флага предлагать .mkv — обещать то, что кончится отказом
   ПОСЛЕ отправки полутора гигабайт. Константа VIDEO_ACCEPT оставлена как «всё,
   что сервер в принципе может принять» — для тестов зеркала списков. */
export const VIDEO_ACCEPT = [...PLAYABLE_ACCEPT, ...CONVERT_ACCEPT].join(',');
export function videoAccept(user) {
  return (user && user.academy_video_convert ? [...PLAYABLE_ACCEPT, ...CONVERT_ACCEPT]
    : PLAYABLE_ACCEPT).join(',');
}
export const POSTER_ACCEPT = 'image/png,image/jpeg,image/webp';
export const posterHint = 'png / jpg / webp — кадр-заставка до нажатия «play»';
export const uploadMaterial = (id, file) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${id}/materials/`, { method: 'POST', body: fileForm(file) }));
export const deleteMaterial = (id) =>
  unwrap(apiFetch(`/api/academy/manage/materials/${id}/`, { method: 'DELETE' }));

// ── тест / вопросы ──────────────────────────────────────────────────────────────
export const saveQuiz = (lessonId, payload) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${lessonId}/quiz/`, { method: 'PUT', body: payload }));
export const deleteQuiz = (lessonId, force) =>
  unwrapDelete(apiFetch(`/api/academy/manage/lessons/${lessonId}/quiz/` + (force ? '?force=1' : ''), { method: 'DELETE' }));
export const createQuestion = (quizId, payload) =>
  unwrap(apiFetch(`/api/academy/manage/quizzes/${quizId}/questions/`, { method: 'POST', body: payload }));
export const updateQuestion = (id, payload) =>
  unwrap(apiFetch(`/api/academy/manage/questions/${id}/`, { method: 'PATCH', body: payload }));
export const deleteQuestion = (id) =>
  unwrap(apiFetch(`/api/academy/manage/questions/${id}/`, { method: 'DELETE' }));

// ── ДЗ (конструктор) ────────────────────────────────────────────────────────────
export const saveHomework = (lessonId, payload) =>
  unwrap(apiFetch(`/api/academy/manage/lessons/${lessonId}/homework/`, { method: 'PUT', body: payload }));
export const deleteHomework = (lessonId, force) =>
  unwrapDelete(apiFetch(`/api/academy/manage/lessons/${lessonId}/homework/` + (force ? '?force=1' : ''), { method: 'DELETE' }));

// ── записи ──────────────────────────────────────────────────────────────────────
// opts.tariffId: число = фильтр по тарифу; 'none' = без тарифа; пусто = все (#4)
// opts.q: строка поиска (email/ФИО/телефон, частичное совпадение) — шлём как ?q= при наличии (B3)
// ВНИМАНИЕ: ответ — ПЛОСКИЙ массив записей (не пагинированный), читать как массив (не data.results).
export const listEnrollments = (courseId, opts = {}) => {
  const params = [];
  const tid = opts && opts.tariffId;
  if (tid !== undefined && tid !== null && tid !== '') params.push(`tariff_id=${encodeURIComponent(tid)}`);
  const q = opts && opts.q;
  if (q != null && String(q).trim() !== '') params.push(`q=${encodeURIComponent(String(q).trim())}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  return unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/enrollments/${qs}`));
};
// карточка ученика: прогресс по урокам + другие курсы (#13)
export const getStudentDetail = (enrollmentId) =>
  unwrap(apiFetch(`/api/academy/manage/enrollments/${enrollmentId}/detail/`));

/* ── ручной зачёт урока ученику (методист) ────────────────────────────────────
   Обучение бывает и вне платформы (очный тренинг, зачёт наставнику), а
   требования урока описывают только обучение В платформе. Пишем ОСНОВАНИЕ, а не
   статус: статус остаётся производным, его пересчитывает бэк.
   Ответ: { status, completed_at, manual, manual_note, progress_pct,
   enrollment_status } — процент и статус записи нужны, чтобы карточка не рисовала
   новый статус урока рядом со старым процентом курса.
   Зачёт последнего незакрытого урока на курсе с сертификатом бэк отдаёт кодом
   'will_issue_certificate' — консоль показывает предупреждение и повторяет с
   ?force=1 (та же схема, что у сброса сданного теста). */
export const setLessonCompletion = (enrollmentId, lessonId, { completed, note, force } = {}) =>
  unwrap(apiFetch(
    `/api/academy/manage/enrollments/${enrollmentId}/lessons/${lessonId}/progress/`
    + (force ? '?force=1' : ''),
    { method: 'POST', body: { completed: !!completed, note: note || '' } }));
export const WILL_ISSUE_CERT_CODE = 'will_issue_certificate';
export function isWillIssueCertGuard(e) {
  return !!e && e.code === WILL_ISSUE_CERT_CODE;
}

/* ── PL-8: сертификат записи (методист) ───────────────────────────────────────
   Оба действия — POST на один ресурс с разным `action` (CertificateManageView).
   Ответ у обоих ОДИН: payload сертификата
   { public_id, serial, issued_at, revoked_at, revoked_reason, reissue_count },
   тот же, что приезжает в getStudentDetail().certificate.
   Причина ОБЯЗАТЕЛЬНА на бэке у отзыва (services.revoke_certificate →
   code='reason_required'): аннулирование без объяснения ученику нечем оспорить.
   Перевыпуск меняет public_id — СТАРАЯ публичная ссылка перестаёт открываться. */
const CERT_ACTION = (enrollmentId, action) =>
  `/api/academy/manage/enrollments/${enrollmentId}/certificate/${action}/`;
export const revokeCertificate = (enrollmentId, reason) =>
  unwrap(apiFetch(CERT_ACTION(enrollmentId, 'revoke'), { method: 'POST', body: { reason } }));
export const reissueCertificate = (enrollmentId, reason) =>
  unwrap(apiFetch(CERT_ACTION(enrollmentId, 'reissue'), { method: 'POST', body: { reason } }));
// Публичный адрес сертификата — тот же, что открывает ученик по ссылке из письма.
export const certificateUrl = (publicId) => `/certificate/${publicId}`;
// заказы Академии для МОП: {status,course_id,q,limit,offset} (#10)
export const getOrders = (params = {}) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return unwrap(apiFetch('/api/academy/manage/orders/' + (qs ? `?${qs}` : '')));
};
/* AD-11: заказ — рабочее место, а не витрина.
   Пути собраны здесь одной константой: три действия ходят в один ресурс, и при
   правке префикса не должно остаться забытой строки. Все три отвечают ОДНИМ
   объектом заказа в контракте getOrders().results[]; reissue дополнительно
   отдаёт ссылку на оплату, чтобы её можно было отправить покупателю. */
const ORDER_ACTION = (id, action) => `/api/academy/manage/orders/${id}/${action}/`;

/* AD-11, блокер. Какие действия над заказом реально МЕНЯЮТ строку списка.
   «Перевыставить» не меняет ничего видимого — заказ как был `pending`, так и
   остаётся, — но консоль всё равно перезапрашивала список тем же тиком. Рефетч
   снимает таблицу целиком (QueryBoundary на время загрузки показывает лоадер),
   а вместе с ней — строку заказа, в чьём стейте лежала ссылка на оплату. Окно
   со ссылкой жило кадр, и адрес, только что выпущенный для клиента, забрать
   было уже неоткуда: второго такого ответа сервер не даст. */
const ORDER_ACTION_RELOADS = { reissue: false, cancel: true, 'mark-paid': true };
export function orderActionNeedsReload(action) {
  // Неизвестное действие перечитываем: лишний запрос дешевле устаревшей таблицы.
  return ORDER_ACTION_RELOADS[action] !== false;
}
// «Перевыставить ссылку» → { order, invoice_id, redirect_url }
export const reissueOrder = (id) =>
  unwrap(apiFetch(ORDER_ACTION(id, 'reissue'), { method: 'POST' }));
// «Отменить» → заказ в статусе cancelled (иначе брошенные pending копятся вечно)
export const cancelOrder = (id) =>
  unwrap(apiFetch(ORDER_ACTION(id, 'cancel'), { method: 'POST' }));
/* «Отметить оплаченным вручную» (оплата по счёту мимо Робокассы) → succeeded +
   фулфилмент записи. Это ДЕНЕЖНЫЙ путь мимо провайдера, поэтому основание
   обязательно: бэк кладёт его в meta заказа (`manual_paid_comment` /
   `manual_paid_invoice_no` рядом с `manual_paid_by/at`) — без него при сверке
   денег видно только «кто-то из методистов открыл платный доступ».
   opts: строка = основание (совместимость), объект = { comment, invoiceNo }. */
export const markOrderPaid = (id, opts) => {
  const o = (opts && typeof opts === 'object') ? opts : { comment: opts };
  const body = {};
  const comment = (o.comment || '').trim();
  const invoiceNo = (o.invoiceNo || '').trim();
  if (comment) body.comment = comment;
  if (invoiceNo) body.invoice_no = invoiceNo;
  return unwrap(apiFetch(ORDER_ACTION(id, 'mark-paid'), { method: 'POST', body }));
};
// глобальный список учеников Академии: {q, limit, offset} — LimitOffset-пагинация (B4).
// Ответ: { count, next, previous, results:[{ id, name, email, phone, enrollment_count,
//   enrollments:[{ id, course_id, course_title, status, progress_pct, enrolled_at, tariff }] }] }.
export const listStudents = (params = {}) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return unwrap(apiFetch('/api/academy/manage/students/' + (qs ? `?${qs}` : '')));
};
export const enrollUsers = (courseId, payload) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/enrollments/`, { method: 'POST', body: payload }));
// Отписка ученика. `force` — как у удаления контента: первый заход упирается в
// гард работ (`has_student_data`), второй подтверждает. Выданный сертификат
// force НЕ пропускает (код `certificate_issued`) — сначала отзыв с причиной.
// unwrapDelete, а НЕ unwrap: только он переносит `students` из тела отказа в
// ошибку. С `unwrap` счётчик терялся, и диалог спрашивал «У учеников есть работы»
// вместо «У 1 ученика есть работы» — то есть просил подтвердить, не сказав объём.
export const deleteEnrollment = (id, force) =>
  unwrapDelete(apiFetch(`/api/academy/manage/enrollments/${id}/` + (force ? '?force=1' : ''),
    { method: 'DELETE' }));
// управление сроком доступа (A3): {action:'freeze'|'unfreeze'} и/или {expires_at}
export const patchEnrollment = (id, payload) =>
  unwrapDelete(apiFetch(`/api/academy/manage/enrollments/${id}/`, { method: 'PATCH', body: payload }));
// выгрузка учеников (CSV) — прямой URL (скачивание через браузер с сессионной кукой)
export const enrollmentsExportUrl = (courseId) =>
  `/api/academy/manage/courses/${courseId}/enrollments/export/`;
export const getCourseAnalytics = (courseId) =>
  unwrap(apiFetch(`/api/academy/manage/courses/${courseId}/analytics/`));

/* ── человеческие подписи консоли (CU-6, AD-8) ─────────────────────────────────
   Живут рядом с клиентом по той же причине, что и studentDataPrompt: это тексты,
   зеркалящие коды бэкенда, и держать их надо в одном месте с контрактом, а не
   растаскивать по трём экранам. Чистые функции — проверяются тестом. */

// lesson_lock (services.lesson_lock) → причина для КУРАТОРА, а не для ученика:
// ему важно «почему у человека не идёт», а не «что вам делать».
const LOCK_TEXT = {
  frozen: 'Закрыт: доступ ученика заморожен',
  // Не «заморожен»: заморозку ставит методист, а это — кончившаяся подписка
  // Restorium. Куратору важно различать, к кому идти с вопросом.
  subscription: 'Закрыт: подписка Restorium не активна',
  tariff: 'Закрыт: не входит в тариф',
  sequential: 'Закрыт: не пройден предыдущий обязательный урок',
};
export function lockReasonText(lock, openAt) {
  if (!lock) return '';
  if (lock === 'drip') {
    const d = openAt ? new Date(openAt) : null;
    if (d && !isNaN(d)) {
      return 'Откроется ' + d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    }
    return 'Закрыт: модуль ещё не открылся по расписанию';
  }
  return LOCK_TEXT[lock] || 'Закрыт';
}

// AD-8. Правки, меняющие требования курса, откатывают завершивших в «Активна» и
// обнуляют completed_at. Раньше это происходило молча — методист узнавал по
// расхождению цифр. Текст диалога называет число записей, которых это коснётся.
export function requirementImpactPrompt(completed, what) {
  const base = `Это изменит требования курса: ${what}.`;
  if (!completed) return `${base} Курс уже опубликован — правка применится к живым ученикам.`;
  // Винительный одушевлённый = родительный: 1 ученика · 2-4 учеников · 5+ учеников.
  // Прошлая версия знала только «1» и выдавала «вернёт 2 учеников».
  return `${base} Это вернёт ${completed} ${studentsWordGen(completed)} в статус «Активна»`
    + ' и обнулит дату завершения. Ранее выданные сертификаты останутся действительными.';
}

/* AD-8, второй случай: аналитика курса не ответила, и число выпускников НЕИЗВЕСТНО.
   Молча продолжать нельзя — именно молчаливый откат завершивших в «Активна» это
   требование и заводилось предотвратить. Спрашиваем честно: «сколько — не знаем». */
export function requirementImpactUnknownPrompt(what) {
  return `Это изменит требования курса: ${what}. Сколько учеников уже завершили курс,`
    + ' сейчас неизвестно — аналитика курса не ответила. Если завершившие есть, они'
    + ' вернутся в статус «Активна», а дата завершения обнулится.';
}

/* ── Вкладки редактора курса и права на них ────────────────────────────────────
   Куратор (право review_homework БЕЗ manage_academy) видел все шесть вкладок и
   «Нет доступа» в теле каждой: интерфейс обещал шесть рабочих мест и отказывал
   на каждом — хуже, чем не показать их вовсе. Набор живёт здесь, а не в
   компоненте, потому что это ЗЕРКАЛО гейтов бэкенда (весь `manage/*` закрыт
   IsAcademyManager; куратору открыта аналитика курса на чтение), и единственное,
   что удержит зеркало от расхождения, — тест на этой чистой функции. */
export const COURSE_EDITOR_TABS = [
  { id: 'settings', label: 'Настройки', icon: 'settings', manage: true },
  { id: 'structure', label: 'Структура', icon: 'layers', manage: true },
  { id: 'landing', label: 'Лендинг', icon: 'globe', manage: true },
  { id: 'tariffs', label: 'Тарифы', icon: 'tag', manage: true },
  { id: 'students', label: 'Ученики', icon: 'user', manage: true },
  { id: 'analytics', label: 'Аналитика', icon: 'analytics', manage: false },
];
export function courseEditorTabs(canManage) {
  return COURSE_EDITOR_TABS.filter((t) => canManage || !t.manage);
}

/* ── Потолок видеофайла урока ──────────────────────────────────────────────────
   ИСТОЧНИК ПРАВДЫ — бэкенд (`settings.ACADEMY_VIDEO_MAX_BYTES`, сейчас 1,5 ГБ =
   1536 МБ). Раньше лимит наружу не отдавался, и подсказка загрузчика жила
   отдельным литералом «до 512 МБ» — методист узнавал настоящий предел ПОСЛЕ
   того, как файл целиком уехал на сервер и вернулся 400. Теперь значение
   приезжает в /api/auth/me полем `academy_video_max_mb`, а константа ниже —
   только фолбэк на случай старого бэка. Проверяем размер ДО отправки. */
export const VIDEO_MAX_MB = 1536;
export const VIDEO_MAX_BYTES = VIDEO_MAX_MB * 1024 * 1024;
/** Лимит в МБ: серверный, если приехал; иначе фолбэк. `user` — объект из /me. */
export function videoMaxMb(user) {
  const n = Number(user && user.academy_video_max_mb);
  return Number.isFinite(n) && n > 0 ? n : VIDEO_MAX_MB;
}
/* «1,5 ГБ» вместо «1536 МБ». Зеркало бэкового `_size_label()` из
   backend/academy/views.py — подсказка и текст отказа сервера обязаны звучать
   одинаково, иначе методист решит, что это два разных лимита. Порог ровно 1024
   МБ: ниже него гигабайты дали бы «0,3 ГБ» и потеряли точность. */
export function sizeLabel(mb) {
  const n = Math.max(0, Math.round(Number(mb) || 0));
  if (n < 1024) return `${n} МБ`;
  return `${String((n / 1024).toFixed(1)).replace(/\.0$/, '').replace('.', ',')} ГБ`;
}
/* Форматы в подсказке — из того же списка, что и `accept` у поля выбора файла.
   Пока это был литерал «mp4 / webm / mov», он разъехался с сервером (m4v/ogv/ogg
   тоже принимаются): методист считал рабочий формат запрещённым и перекодировал
   файл впустую. mp4 стоит первым намеренно — он единственный, который играет
   везде, включая iPhone. */
export const videoLimitHint = (user) =>
  `${VIDEO_EXTS.join(' / ')}, до ${sizeLabel(videoMaxMb(user))}`
  + (user && user.academy_video_convert ? ' · mkv и avi сконвертируем сами' : '');
export function videoTooLargeText(size, user) {
  const mb = Math.max(1, Math.round((Number(size) || 0) / (1024 * 1024)));
  return `Файл ${sizeLabel(mb)} — больше предела в ${sizeLabel(videoMaxMb(user))}.`
    + ' Сервер такой не примет: сожмите видео или разбейте урок на части.';
}

/* ── AD-6: разбор массовой записи по email ─────────────────────────────────────
   Бэк отдаёт РАЗБОР по адресам: {enrolled:[…], invited:[…], failed:[{email,
   reason}]}. Старый контракт — {enrolled: <число>, enrollments:[…]}: адресов в
   нём нет вовсе, и подставлять вместо них введённый список нельзя — методист
   решил бы, что прошли все. Поэтому у результата есть флаг `detailed`: без него
   панель честно показывает только счётчик записанных.
   Нормализация здесь, а не в экране: это форма ОТВЕТА, а не вёрстка. */
function enrollEntry(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const email = raw.trim();
    return email ? { email, name: '', reason: '', already: false } : null;
  }
  if (typeof raw !== 'object') return null;
  const email = String(raw.email || raw.user_email || '').trim();
  const name = String(raw.name || raw.user_label || '').trim();
  // `reason` бэк присылает уже по-русски, `code` — машинный. Берём человеческий
  // текст первым: подменять присланное объяснение своим словарём — терять смысл.
  const reason = String(raw.reason || raw.detail || raw.code || raw.message || '').trim();
  if (!email && !name) return null;
  // already_enrolled: человек и до этого был на курсе. Без пометки методист
  // считает «Записано: 10» новыми учениками и не понимает, почему список не вырос.
  return { email: email || name, name, reason, already: !!raw.already_enrolled };
}
export function normalizeEnrollResult(data) {
  const d = (data && typeof data === 'object') ? data : {};
  const list = (v) => (Array.isArray(v) ? v.map(enrollEntry).filter(Boolean) : []);
  const enrolled = list(d.enrolled);
  const invited = list(d.invited);
  const failed = list(d.failed);
  // Разбор считаем полученным, только если хоть один ключ приехал СПИСКОМ.
  const detailed = Array.isArray(d.enrolled) || Array.isArray(d.invited)
    || Array.isArray(d.failed);
  const legacyCount = typeof d.enrolled === 'number' ? d.enrolled : 0;
  return {
    detailed,
    enrolled, invited, failed,
    counts: {
      enrolled: detailed ? enrolled.length : legacyCount,
      invited: invited.length,
      failed: failed.length,
    },
  };
}

// Причина отказа по адресу: коды бэкенда → человеческий текст. Незнакомый код
// показываем как есть — это лучше, чем «Ошибка» вместо присланного объяснения.
const ENROLL_FAIL_TEXT = {
  not_found: 'Аккаунта с таким адресом нет',
  no_user: 'Аккаунта с таким адресом нет',
  invalid_email: 'Адрес введён с ошибкой',
  bad_email: 'Адрес введён с ошибкой',
  already_enrolled: 'Уже записан на этот курс',
  invite_failed: 'Приглашение не отправилось',
};
export function enrollFailureText(reason) {
  const key = String(reason == null ? '' : reason).trim();
  if (!key) return 'Не удалось записать';
  return ENROLL_FAIL_TEXT[key] || key;
}

// Сводка для тоста. Нули не печатаем: «Приглашено: 0 · Ошибок: 0» — шум,
// а вот «Ошибок: 3» обязано попасть в глаза сразу.
export function enrollSummaryText(result) {
  const c = (result && result.counts) || {};
  const parts = [`Записано: ${c.enrolled || 0}`];
  if (c.invited) parts.push(`Приглашено: ${c.invited}`);
  if (c.failed) parts.push(`Ошибок: ${c.failed}`);
  return parts.join(' · ');
}
